/**
 * BrushControls — トキワ荘の筆・色ツールバー（共通実装）
 *
 * 共有キャンバス（RT-05 / TokiwaRooms）とソロスケッチ（TKW-01/02 / SketchCanvas）で共用する。
 * 2026-08-14 拡充（オーナー要望: お絵描きの森風の色・筆バリエーション＋細い筆の選びやすさ）。
 *
 * サーバー側の制約（CanvasRoomDO・実測）: color は32文字まで（パレット検証なし）、
 * width は 1〜64 にクランプ。ここのプリセットは必ずその範囲内に収めること。
 *
 * 設計メモ:
 * - 太さはスライダーをやめて5段のプリセット（極細1〜極太14）。ドットの見た目が
 *   そのまま太さを表すので、スライダーより「いま何を選んでいるか」が分かる。
 * - 色は15色パレット＋カスタム色（ネイティブのカラーピッカー）。
 * - 色・太さはツール選択ではなく属性なので、調停原則4（意思決定4つ以内）の
 *   カウント対象外（ナビ・パレット類は慣習に従う）。タップ領域は44px以上を維持。
 */
import { FC, ReactNode, useState } from 'react';

export type BrushTool = 'pen' | 'eraser';

/** 15色パレット（墨〜肌色）。描画データとして送るのはユーザーの作画色なので役割色ルールの対象外 */
export const BRUSH_COLORS: string[] = [
    '#241c2e', // 墨
    '#6b7280', // ねずみ
    '#92400e', // 茶
    '#fcd7b8', // 肌
    '#ef4444', // 紅
    '#f97316', // 橙
    '#f59e0b', // 山吹
    '#facc15', // 黄
    '#84cc16', // 若草
    '#22c55e', // 緑
    '#06b6a4', // 青緑
    '#0ea5e9', // 空
    '#3b82f6', // 青
    '#8b5cf6', // 菫
    '#ec4899', // 桃
];

/** 筆の太さ5段。value はサーバークランプ範囲(1〜64)内 */
export const BRUSH_SIZES: { label: string; value: number }[] = [
    { label: '極細', value: 1 },
    { label: '細', value: 2 },
    { label: '中', value: 4 },
    { label: '太', value: 8 },
    { label: '極太', value: 14 },
];

export const BrushControls: FC<{
    tool: BrushTool;
    color: string;
    width: number;
    onTool: (t: BrushTool) => void;
    onColor: (c: string) => void;
    onWidth: (w: number) => void;
    /** 末尾に置く操作（取り消し・クリア等）。呼び出し元ごとに異なるためスロットにする */
    trailing?: ReactNode;
}> = ({ tool, color, width, onTool, onColor, onWidth, trailing }) => {
    // カスタム色の現在値（パレット外の色を選んだ状態を保持して見せる）
    const [custom, setCustom] = useState('#241c2e');
    const isCustomActive = tool === 'pen' && !BRUSH_COLORS.includes(color);

    return (
        <div className="mb-2">
            <div className="flex flex-wrap items-center gap-2">
                <button aria-pressed={tool === 'pen'} onClick={() => onTool('pen')}
                    className={`btn btn-sm min-h-[44px] ${tool === 'pen' ? 'btn-primary' : 'btn-ghost'}`}>ペン</button>
                <button aria-pressed={tool === 'eraser'} onClick={() => onTool('eraser')}
                    className={`btn btn-sm min-h-[44px] ${tool === 'eraser' ? 'btn-primary' : 'btn-ghost'}`}>消しゴム</button>

                {/* 太さ: 5段プリセット。ドットの直径＝実際の太さの目安 */}
                <span className="flex items-center gap-1 ml-1" role="group" aria-label="筆の太さ">
                    {BRUSH_SIZES.map(s => (
                        <button
                            key={s.value}
                            onClick={() => onWidth(s.value)}
                            aria-pressed={width === s.value}
                            aria-label={`太さ ${s.label}`}
                            title={s.label}
                            className={`w-11 h-11 min-h-[44px] min-w-[44px] rounded-full border-2 flex items-center justify-center ${width === s.value ? 'border-ink bg-cream-2' : 'border-ink/20 bg-surface'}`}
                        >
                            <span
                                aria-hidden="true"
                                className="rounded-full bg-ink"
                                style={{ width: Math.max(3, Math.min(22, s.value + 2)), height: Math.max(3, Math.min(22, s.value + 2)) }}
                            />
                        </button>
                    ))}
                </span>

                {trailing && <span className="ml-auto flex items-center gap-2">{trailing}</span>}
            </div>

            {/* 色: 15色＋カスタム。色を選ぶと自動でペンに戻る（消しゴムのまま色を選ぶ状況を作らない） */}
            <div className="flex flex-wrap items-center gap-1 mt-2" role="group" aria-label="筆の色">
                {BRUSH_COLORS.map(c => (
                    <button
                        key={c}
                        onClick={() => { onColor(c); onTool('pen'); }}
                        aria-label={`色 ${c}`}
                        aria-pressed={tool === 'pen' && color === c}
                        className={`w-11 h-11 min-h-[44px] min-w-[44px] rounded-full border-2 ${tool === 'pen' && color === c ? 'border-ink' : 'border-ink/20'}`}
                        style={{ backgroundColor: c }}
                    />
                ))}
                {/* カスタム色（自由な1色）。サーバーは任意の色文字列(≤32字)を受けるためフロントだけで完結する */}
                <label
                    className={`w-11 h-11 min-h-[44px] min-w-[44px] rounded-full border-2 cursor-pointer overflow-hidden relative ${isCustomActive ? 'border-ink' : 'border-ink/20'}`}
                    title="すきな色"
                >
                    <span className="sr-only">すきな色を選ぶ</span>
                    <span
                        aria-hidden="true"
                        className="absolute inset-0"
                        style={{ background: isCustomActive ? color : 'conic-gradient(#ef4444, #f59e0b, #22c55e, #0ea5e9, #8b5cf6, #ec4899, #ef4444)' }}
                    />
                    <input
                        type="color"
                        value={isCustomActive ? color : custom}
                        onChange={e => { setCustom(e.target.value); onColor(e.target.value); onTool('pen'); }}
                        className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                        aria-label="すきな色を選ぶ"
                    />
                </label>
            </div>
        </div>
    );
};
